import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, useJSONLD } from '@/hooks/useSEO';
import { categories, categoryMeta } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'platform-intro');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeSlug);
  const meta = categoryMeta[activeSlug] || categoryMeta['platform-intro'];
  const categoryArticles = getArticlesByCategory(activeSlug);

  useSEO({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonicalUrl: `https://91cn.com/category/${activeSlug}`,
  });

  useJSONLD({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category?.name || '分类页面',
    description: meta.description,
    url: `https://91cn.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91.cn com 官方入口',
      url: 'https://91cn.com',
    },
  });

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: category.name },
        ]} />

        {/* Category Header */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-14 h-14 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
            <p className="text-sm text-foreground-400">{category.articleCount} 篇相关文章</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full py-6 bg-background-100 border-b border-background-200/60 sticky top-16 z-40">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group p-5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-all duration-200 cursor-pointer block"
                  >
                    <div className="w-full h-40 rounded-md overflow-hidden mb-4 bg-background-200">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={article.title}
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-1 rounded-md bg-accent-100 text-accent-700 font-medium whitespace-nowrap">{article.categoryName}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h2 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">{article.title}</h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                      <i className="ri-calendar-line"></i>
                      <span>{article.publishDate}</span>
                      <span className="mx-1">·</span>
                      <i className="ri-user-line"></i>
                      <span>{article.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-file-list-3-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无文章，请选择其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}